import {filterListType} from "./types/filters";

export interface FilterLimit {
    type: 'whitelist' | 'blacklist' | 'gte' | 'lte';
    values?: string[];
    value?: string;
}

export type ConditionValue = string | { not: string } | { gt: string } | { lt: string } | { gte: string } | { lte: string };

export interface FilterRule {
    condition: Record<string, ConditionValue>;
    limits: Record<string, FilterLimit>;
}

export class FilterLimiter {
    private rules: FilterRule[] = [
        {
            condition: { color: "YELLOW" },
            limits: {
                metal: { type: 'blacklist', values: ["AG925", "PT950"] }
            }
        },
        {
            condition: { color: "RED" },
            limits: {
                metal: { type: 'blacklist', values: ["AG925", "PT950"] }
            }
        },
        {
            condition: { stone_type: "lab" },
            limits: {
                stone_quality: { type: 'whitelist', values: ["lab-evs2", "lab-eif"] }
            }
        },
        {
            condition: { stone_type: "earth" },
            limits: {
                stone_quality: { type: 'blacklist', values: ["lab-evs2", "lab-eif"] }
            }
        },
        {
            condition: { stone_form: { not: "ROUND" } },
            limits: {
                stone_quality: { type: 'whitelist', values: ["twvs", "twif"] }
            }
        },
        {
            condition: { stone_size: { gte: "0.3" } },
            limits: {
                stone_quality: { type: 'blacklist', values: ["wsi"] }
            }
        },
        {
            condition: { stone_size: { lt: "0.3" } },
            limits: {
                stone_quality: { type: 'blacklist', values: ["dif"] }
            }
        },
        {
            condition: { stone_form: { not: "ROUND" }, stone_type: "earth" },
            limits: {
                stone_size: { type: 'gte', value: "0.2" }
            }
        },
        {
            condition: { stone_form: { not: "ROUND" }},
            limits: {
                stone_size: { type: 'blacklist', values: ["0.250"] }
            }
        },
        {
            condition: { stone_form: { not: "ROUND" }, stone_type: "lab" },
            limits: {
                stone_size: { type: 'gte', value: "0.5" }
            }
        },
        {
            condition: { price: { not: "" } },
            limits: {
                price_sort: { type: 'blacklist', values: ["min-price"] }
            }
        },
        {
            condition: { metal: { not: "" } },
            limits: {
                price_sort: { type: 'blacklist', values: ["min-price"] }
            }
        }
    ];

    /**
     * Limits the filters based on the active filters and defined rules.
     *
     * @param filters The list of available filters and their values.
     * @param activeFilters The currently selected filters.
     * @returns A new filterListType with limited values.
     */
    public limitFilters(filters: filterListType, activeFilters: Record<string, string>): filterListType {
        // Deep copy filters to avoid modifying the original prop
        const limitedFilters: filterListType = JSON.parse(JSON.stringify(filters));

        for (const rule of this.rules) {
            if (this.matchesCondition(rule.condition, activeFilters)) {
                for (const [filterName, limit] of Object.entries(rule.limits)) {
                    if (limitedFilters[filterName]) {
                        limitedFilters[filterName].values = this.applyLimit(
                            limitedFilters[filterName].values,
                            limit
                        );

                        if (limitedFilters[filterName].prio_values) {
                            limitedFilters[filterName].prio_values = this.applyLimit(
                                limitedFilters[filterName].prio_values!,
                                limit
                            );
                        }
                    }
                }
            }
        }

        return limitedFilters;
    }

    /**
     * Checks and cleans the active filters based on the defined rules.
     * If an active filter value is blacklisted or not whitelisted, it's removed.
     *
     * @param activeFilters The currently selected filters.
     * @returns A new Record<string, string> with valid filter values.
     */
    public checkActiveFilters(activeFilters: Record<string, string>): Record<string, string> {
        const cleanedActiveFilters: Record<string, string> = {...activeFilters};
        let hasChanges = true;

        // Iterate until stable, in case removing one filter affects other conditions
        while (hasChanges) {
            hasChanges = false;
            for (const rule of this.rules) {
                if (this.matchesCondition(rule.condition, cleanedActiveFilters)) {
                    for (const [filterName, limit] of Object.entries(rule.limits)) {
                        const activeValue = cleanedActiveFilters[filterName];
                        if (activeValue) {
                            if (!this.isAllowed(activeValue, limit)) {
                                delete cleanedActiveFilters[filterName];
                                hasChanges = true;
                            }
                        }
                    }
                }
            }
        }

        return cleanedActiveFilters;
    }

    private isAllowed(value: string, limit: FilterLimit): boolean {
        switch (limit.type) {
            case 'whitelist':
                return limit.values?.includes(value) ?? false;
            case 'blacklist':
                return !(limit.values?.includes(value) ?? false);
            case 'gte':
                return parseFloat(value) >= parseFloat(limit.value!);
            case 'lte':
                return parseFloat(value) <= parseFloat(limit.value!);
            default:
                return true;
        }
    }

    private matchesCondition(condition: Record<string, ConditionValue>, activeFilters: Record<string, string>): boolean {
        return Object.entries(condition).every(([key, conditionValue]) => {
            const activeValue = activeFilters[key];
            if (activeValue === undefined) return false;

            if (typeof conditionValue === 'string') {
                return activeValue === conditionValue;
            }

            if (conditionValue && typeof conditionValue === 'object') {
                if ('not' in conditionValue) {
                    return activeValue !== conditionValue.not;
                }

                if (key === 'stone_size') {
                    const numericActive = parseFloat(activeValue);
                    if ('gt' in conditionValue) return numericActive > parseFloat(conditionValue.gt);
                    if ('lt' in conditionValue) return numericActive < parseFloat(conditionValue.lt);
                    if ('gte' in conditionValue) return numericActive >= parseFloat(conditionValue.gte);
                    if ('lte' in conditionValue) return numericActive <= parseFloat(conditionValue.lte);
                }
            }

            return false;
        });
    }

    private applyLimit(values: string[], limit: FilterLimit): string[] {
        return values.filter(v => this.isAllowed(v, limit));
    }
}
